/**
 * Templates Tab Component
 * Displays list of templates - click to navigate to template detail page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridActionsCellItem,
} from '@mui/x-data-grid-pro';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglSecondarySmallButton } from 'components/button/BglButtons';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { Template } from '../types/configRegistryTypes';
import { CloneTemplateDialog } from './CloneTemplateDialog';
import { createColumnHeader } from './ColumnHeader';

export const TemplatesTab = () => {
  const { rc } = useGlobalConfig();
  const navigate = useNavigate();
  const theme = useTheme();

  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const canEdit = rc.permissionsManager.canTemplateEdit();

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await ConfigRegistryApis.getTemplates(rc);
      if (response.error) {
        setError(response.error.message || 'Failed to fetch templates');
      } else if (response.data) {
        // Handle Strapi response structure
        const data = (response.data as any).data || response.data;
        setTemplates(Array.isArray(data) ? data : []);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [rc]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleClone = (template: Template) => {
    setSelectedTemplate(template);
    setCloneDialogOpen(true);
  };

  const handleTemplateClick = (template: Template) => {
    navigate(`/cms/config-registry/templates/${template.documentId}`);
  };

  const handleTemplateUpdated = () => {
    fetchTemplates();
  };

  const columns: GridColDef[] = [
    {
      field: 'serialNo',
      headerName: '#',
      width: 70,
      sortable: false,
      hideable: false,
      resizable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const rowIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
        return (
          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {rowIndex + 1}
          </Typography>
        );
      },
    },
    {
      field: 'name',
      headerName: 'Template Name',
      flex: 1.5,
      minWidth: 200,
      hideable: false,
      resizable: true,
      renderHeader: createColumnHeader('Template Name', 'name'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || ''} placement="top" arrow>
          <Box sx={{
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            color: 'primary.main',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {params.value}
          </Box>
        </Tooltip>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 250,
      resizable: true,
      renderHeader: createColumnHeader('Description', 'description'),
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value || 'No description'} placement="top" arrow>
          <Typography variant="body2" color="text.secondary" noWrap>
            {params.value || '-'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'module',
      headerName: 'Module',
      width: 120,
      resizable: true,
      renderHeader: createColumnHeader('Module', 'module'),
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Tooltip title={params.value} placement="top" arrow>
            <Chip label={params.value} size="small" />
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.disabled">-</Typography>
        )
      ),
    },
    {
      field: 'items',
      headerName: '# Configs',
      width: 130,
      resizable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: createColumnHeader('# Configs', 'numConfigs'),
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value?.length || 0}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      pinnable: true,
      hideable: false,
      renderHeader: createColumnHeader('Actions', 'actions'),
      cellClassName: 'actions-column',
      headerClassName: 'actions-column-header',
      getActions: ({ row }) => [
        <GridActionsCellItem
          key="view"
          icon={<Tooltip title="View Template"><VisibilityIcon /></Tooltip>}
          label="View"
          onClick={() => handleTemplateClick(row as Template)}
        />,
        <GridActionsCellItem
          key="clone"
          icon={<Tooltip title="Clone Template"><ContentCopyIcon /></Tooltip>}
          label="Clone"
          onClick={() => handleClone(row as Template)}
        />,
      ],
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" action={
          <BglSecondarySmallButton onClick={fetchTemplates}>Retry</BglSecondarySmallButton>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Templates Table */}
      <Box sx={{
        width: '100%',
        height: { xs: 'calc(100vh - 320px)', sm: 'calc(100vh - 280px)' },
        minHeight: 400,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
          : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      }}>
        <DataGridPro
          rows={templates}
          columns={columns}
          getRowId={(row) => row.documentId}
          disableRowSelectionOnClick
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            columns: {
              columnVisibilityModel: {
                module: false,
              },
            },
          }}
          sx={{
            border: 'none',
            bgcolor: 'background.paper',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
              borderBottom: `2px solid ${theme.palette.divider}`,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              },
            },
            '& .MuiDataGrid-row': {
              transition: 'background-color 0.15s ease',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark'
                  ? 'rgba(25, 118, 210, 0.12)'
                  : 'rgba(25, 118, 210, 0.06)',
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}`,
              py: 1,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              '&:focus': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `2px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : '#f8f9fa',
            },
            '& .MuiDataGrid-columnSeparator': {
              display: 'none',
            },
            // Actions column - vertical separator line
            '& .actions-column-header': {
              borderLeft: `2px solid ${theme.palette.divider}`,
            },
            '& .actions-column': {
              borderLeft: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            },
          }}
        />
      </Box>

      {templates.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No templates found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Templates are created by administrators in the CMS admin panel.
          </Typography>
        </Box>
      )}

      {/* Dialogs */}
      {selectedTemplate && (
        <CloneTemplateDialog
          open={cloneDialogOpen}
          onClose={() => setCloneDialogOpen(false)}
          template={selectedTemplate}
          onCloned={handleTemplateUpdated}
        />
      )}
    </Box>
  );
};

export default TemplatesTab;

